import { useEffect } from "react";
import { HOME_MEDIA, mediaUrl, publicMediaSrc, toAbsoluteUrl, toConfiguredAbsoluteUrl } from "../config/api";
import contactHero from "../assets/contact-hero.jpg";

const DEFAULT_DOCUMENT_TITLE = "Steel, Hardware & Industrial Supplies Malaysia";
const DEFAULT_DOCUMENT_DESCRIPTION =
  "Steel, hardware, fasteners, roofing, paint and industrial products for contractors across Malaysia. Request a quote today.";
const PAGE_JSON_LD_ID = "contact-webpage-jsonld";
const ORG_JSON_LD_ID = "contact-organization-jsonld";
const BREADCRUMB_JSON_LD_ID = "contact-breadcrumb-jsonld";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TITLE_MAX = 60;
const DESCRIPTION_MAX = 160;

function cleanText(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "undefined" || text === "null") return "";
  return text.replace(/\s+/g, " ");
}

function upsertMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el && el.dataset.contactSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.dataset.contactSeo = "true";
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el && el.dataset.contactSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  el.dataset.contactSeo = "true";
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function prune(value) {
  if (Array.isArray(value)) {
    const next = value.map(prune).filter((item) => item !== undefined);
    return next.length ? next : undefined;
  }
  if (value && typeof value === "object") {
    const next = {};
    Object.keys(value).forEach((key) => {
      const item = prune(value[key]);
      if (item !== undefined && item !== "") next[key] = item;
    });
    return Object.keys(next).length ? next : undefined;
  }
  if (value === undefined || value === null || value === "") return undefined;
  return value;
}

function socialUrl(value) {
  const raw = cleanText(value);
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw.replace(/^\/+/, "")}`;
}

function schemaTelephone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits ? `+${digits}` : "";
}

function validEmail(value) {
  const email = cleanText(value);
  return EMAIL_RE.test(email) ? email : "";
}

function absolutePublicUrl(path) {
  const configured = toConfiguredAbsoluteUrl(path);
  if (/^https?:\/\//i.test(configured)) return configured;
  return toAbsoluteUrl(path);
}

function shortenWithoutCuttingWord(text, max) {
  const cleaned = cleanText(text);
  if (cleaned.length <= max) return cleaned;
  const words = cleaned.split(" ");
  let next = "";
  for (const word of words) {
    const candidate = next ? `${next} ${word}` : word;
    if (candidate.length > max) break;
    next = candidate;
  }
  return next || cleaned.slice(0, max);
}

export function contactPageSeo(contact) {
  const siteName = cleanText(contact?.siteName);
  if (!siteName) return null;

  const titleCandidates = [
    `Contact & Request a Quote | ${siteName}`,
    `Contact & Quote | ${siteName}`,
    `Contact | ${siteName}`,
  ];
  const title = titleCandidates.find((item) => item.length <= TITLE_MAX) || titleCandidates[titleCandidates.length - 1];

  const descriptionCandidates = [
    `Contact ${siteName} for steel, hardware and industrial supply enquiries, product sourcing and delivery support across Malaysia.`,
    `Contact ${siteName} for steel, hardware and industrial supply enquiries and delivery support across Malaysia.`,
    `Contact ${siteName} for steel, hardware and industrial supply enquiries across Malaysia.`,
  ].map((item) => item.replace(/\s+/g, " ").trim());
  const description =
    descriptionCandidates.find((item) => item.length <= DESCRIPTION_MAX) ||
    shortenWithoutCuttingWord(descriptionCandidates[descriptionCandidates.length - 1], DESCRIPTION_MAX);

  return {
    siteName,
    title,
    description,
    imageAlt: `Contact ${siteName} for industrial supply enquiries`,
  };
}

function clearContactSeo() {
  document.head.querySelectorAll("[data-contact-seo='true']").forEach((el) => el.remove());
  upsertJsonLd(PAGE_JSON_LD_ID, null);
  upsertJsonLd(ORG_JSON_LD_ID, null);
  upsertJsonLd(BREADCRUMB_JSON_LD_ID, null);
}

function applyNeutralMetadata(canonicalUrl) {
  document.title = DEFAULT_DOCUMENT_TITLE;
  upsertMeta("name", "description", DEFAULT_DOCUMENT_DESCRIPTION);
  upsertLink("canonical", canonicalUrl);
  upsertMeta("name", "robots", "index,follow,max-image-preview:large");
  upsertJsonLd(PAGE_JSON_LD_ID, null);
  upsertJsonLd(ORG_JSON_LD_ID, null);
  upsertJsonLd(BREADCRUMB_JSON_LD_ID, null);
}

function ContactSeo({ contact, contactStatus = "loading" }) {
  useEffect(() => {
    const canonicalHref = toConfiguredAbsoluteUrl("/contact");
    const canonicalUrl = absolutePublicUrl("/contact") || canonicalHref;
    const homeUrl = absolutePublicUrl("/") || toConfiguredAbsoluteUrl("/");
    const siteUrl = String(homeUrl || "/").replace(/\/$/, "");
    const websiteId = `${siteUrl}/#website`;
    const organizationId = `${siteUrl}/#organization`;

    if (contactStatus === "loading") {
      applyNeutralMetadata(canonicalHref);
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
        clearContactSeo();
      };
    }

    const seo = contactPageSeo(contact);
    if (!seo) {
      applyNeutralMetadata(canonicalHref);
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
        clearContactSeo();
      };
    }

    const logoPath = publicMediaSrc(contact?.siteLogo) || mediaUrl(HOME_MEDIA.logo);
    const imageUrl = absolutePublicUrl(contactHero) || absolutePublicUrl(logoPath);
    const email = validEmail(contact?.emailAddress);
    const telephone = schemaTelephone(contact?.whatsappNumber);
    const companyRegNo = cleanText(contact?.companyRegNo);
    const sameAs = [socialUrl(contact?.instagram), socialUrl(contact?.facebook)].filter(Boolean);

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertLink("canonical", canonicalHref);
    upsertMeta("name", "robots", "index,follow,max-image-preview:large");

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:site_name", seo.siteName);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:alt", imageUrl ? seo.imageAlt : "");
    upsertMeta("property", "og:locale", "en_MY");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertMeta("name", "twitter:image:alt", imageUrl ? seo.imageAlt : "");

    upsertJsonLd(
      PAGE_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "@id": canonicalUrl ? `${canonicalUrl}#webpage` : undefined,
        url: canonicalUrl || undefined,
        name: seo.title,
        description: seo.description,
        inLanguage: "en-MY",
        isPartOf: {
          "@type": "WebSite",
          "@id": websiteId,
          url: siteUrl || homeUrl || undefined,
          name: seo.siteName,
        },
        about: { "@id": organizationId },
        mainEntity: { "@id": organizationId },
        breadcrumb: canonicalUrl ? { "@id": `${canonicalUrl}#breadcrumb` } : undefined,
      })
    );

    upsertJsonLd(
      ORG_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": organizationId,
        name: seo.siteName,
        url: siteUrl || homeUrl || undefined,
        logo: absolutePublicUrl(logoPath) || undefined,
        identifier: companyRegNo
          ? {
              "@type": "PropertyValue",
              name: "Company Registration Number",
              value: companyRegNo,
            }
          : undefined,
        email: email || undefined,
        telephone: telephone || undefined,
        areaServed: {
          "@type": "Country",
          name: "Malaysia",
        },
        contactPoint:
          telephone || email
            ? [
                {
                  "@type": "ContactPoint",
                  contactType: "sales",
                  telephone: telephone || undefined,
                  email: email || undefined,
                  areaServed: "MY",
                  availableLanguage: ["English", "Malay", "Chinese"],
                },
              ]
            : undefined,
        sameAs: sameAs.length ? sameAs : undefined,
      })
    );

    upsertJsonLd(
      BREADCRUMB_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": canonicalUrl ? `${canonicalUrl}#breadcrumb` : undefined,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          { "@type": "ListItem", position: 2, name: "Contact", item: canonicalUrl },
        ],
      })
    );

    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
      clearContactSeo();
    };
  }, [contact, contactStatus]);

  return null;
}

export default ContactSeo;
